import { createTheme, MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';

const theme = createTheme({
  fontFamily: 'Poppins',
  fontFamilyMonospace: 'Poppins',
  primaryColor: 'brand-cyan',
  colors: {
    dark: [
      '#b0b5c9', // Lightest and least saturated
      '#9399b3',
      '#777e9c',
      '#5e6484',
      '#4b506c',
      '#3c4257',
      '#303748',
      '#252c39',
      '#1b222b',
      '#12171d', // Darkest
    ],
    gray: [
      '#f8f9fa',
      '#f1f3f5',
      '#e9ecef',
      '#dee2e6',
      '#ced4da',
      '#adb5bd',
      '#868e96',
      '#495057',
      '#343a40',
      '#212529',
    ],
    red: [
      '#fff5f5',
      '#ffe3e3',
      '#ffc9c9',
      '#ffa8a8',
      '#ff8787',
      '#ff6b6b',
      '#fa5252',
      '#f03e3e',
      '#e03131',
      '#c92a2a',
    ],
    pink: [
      '#fff0f6',
      '#ffdeeb',
      '#fcc2d7',
      '#faa2c1',
      '#f783ac',
      '#f06595',
      '#e64980',
      '#d6336c',
      '#c2255c',
      '#a61e4d',
    ],
    grape: [
      '#f8f0fc',
      '#f3d9fa',
      '#eebefa',
      '#e599f7',
      '#da77f2',
      '#cc5de8',
      '#be4bdb',
      '#ae3ec9',
      '#9c36b5',
      '#862e9c',
    ],
    violet: [
      '#f3f0ff',
      '#e5dbff',
      '#d0bfff',
      '#b197fc',
      '#9775fa',
      '#845ef7',
      '#7950f2',
      '#7048e8',
      '#6741d9',
      '#5f3dc4',
    ],
    indigo: [
      '#edf2ff',
      '#dbe4ff',
      '#bac8ff',
      '#91a7ff',
      '#748ffc',
      '#5c7cfa',
      '#4c6ef5',
      '#4263eb',
      '#3b5bdb',
      '#364fc7',
    ],
    blue: [
      '#e7f5ff',
      '#d0ebff',
      '#a5d8ff',
      '#74c0fc',
      '#4dabf7',
      '#339af0',
      '#228be6',
      '#1c7ed6',
      '#1971c2',
      '#1864ab',
    ],
    cyan: [
      '#e3fafc',
      '#c5f6fa',
      '#99e9f2',
      '#66d9e8',
      '#3bc9db',
      '#22b8cf',
      '#15aabf',
      '#1098ad',
      '#0c8599',
      '#0b7285',
    ],
    teal: [
      '#e6fcf5',
      '#c3fae8',
      '#96f2d7',
      '#63e6be',
      '#38d9a9',
      '#20c997',
      '#12b886',
      '#0ca678',
      '#099268',
      '#087f5b',
    ],
    green: [
      '#ebfbee',
      '#d3f9d8',
      '#b2f2bb',
      '#8ce99a',
      '#69db7c',
      '#51cf66',
      '#40c057',
      '#37b24d',
      '#2f9e44',
      '#2b8a3e',
    ],
    lime: [
      '#f4fce3',
      '#e9fac8',
      '#d8f5a2',
      '#c0eb75',
      '#a9e34b',
      '#94d82d',
      '#82c91e',
      '#74b816',
      '#66a80f',
      '#5c940d',
    ],
    yellow: [
      '#fff9db',
      '#fff3bf',
      '#ffec99',
      '#ffe066',
      '#ffd43b',
      '#fcc419',
      '#fab005',
      '#f59f00',
      '#f08c00',
      '#e67700',
    ],
    orange: [
      '#fff4e6',
      '#ffe8cc',
      '#ffd8a8',
      '#ffc078',
      '#ffa94d',
      '#ff922b',
      '#fd7e14',
      '#f76707',
      '#e8590c',
      '#d9480f',
    ],
    'brand-cyan': [
      '#D9F1FC', // lightest
      '#B2E3F9',
      '#8AD5F7',
      '#62C8F4',
      '#3ABAEB',
      '#1A98F1', // base color '#1784DA',
      '#45BCFF', // new hover color
      '#146EC3',
      '#0E4296', // darkest
      '#0b3b89',
    ],
    'brand-pink': [
      '#FFE8F3', // lightest
      '#FFC2E0',
      '#FF9DCD',
      '#FF76BB',
      '#FF50A8',
      '#FF29A8', // base color
      '#E12692',
      '#C8237D',
      '#AF2069',
      '#961D55', // darkest
    ],
    'brand-green': [
      '#E6FFF3', // lightest
      '#BFFFE1',
      '#99FFCF',
      '#73FFBE',
      '#4DFFAC',
      '#03F584', // base color
      '#03DB76',
      '#03C269',
      '#03A85B',
      '#028E4D', // darkest
    ],
    utility: [
      '#E0E9F1', // 0 - Light Sapphire
      '#C1C9D2', // 1 - Light Sodalite
      '#A2AAB4', // 2 - Light Azurite
      '#647376', // 3 - Mid Sodalite
      '#164B71', // 4 - Cold Sapphire
      '#0B1833', // 5 - Sodalite
      '#07182C', // 6 - Azurite
      '#041022', // 7 - Dark Delft
      '#060D21', // 8 - Midnight
      '#45BCFF', // 9 - Topaz
    ],
    feedback: [
      '#03F584', // 0 - amazonite-100 (success)
      '#03F58415', // 1 - amazonite-15
      '#F56903', // 2 - amber-100 (warning)
      '#F5690315', // 3 - amber-15
      '#F50312', // 4 - jasper-100 (error)
      '#F5031215', // 5 - jasper-15
      '#1A98F120', // 6 - lapis-20
      '#1A98F150', // 7 - lapis-50
      '#1A98F150', // 8 - Sodalite-50
      '#1A98F170', // 9 - Sodalite-70
    ],
    whitesmoke: [
      '#F3F4FD', // 0 - 100%
      '#F3F4FD70', // 1 - 70%
      '#F3F4FD50', // 2 - 50%
      '#F3F4FD20', // 3 - 20%
      '#F3F4FD10', // 4 - 10%
      '#F3F4FD', // 5 - 100%
      '#F3F4FD70', // 6 - 70%
      '#F3F4FD50', // 7 - 50%
      '#F3F4FD20', // 8 - 20%
      '#F3F4FD10', // 9 - 10%
    ],
  },
  components: {
    Button: {
      defaultProps: {
        radius: 'xl',
      },
    },
    ActionIcon: {},
  },
});

export type ThemeProps = {
  children: React.ReactNode;
};

export function Theme(props: ThemeProps) {
  return (
    <MantineProvider defaultColorScheme='dark' theme={theme}>
      {props.children}
    </MantineProvider>
  );
}
